import { useEffect, useState } from "react";
import axios from "axios";
import { PenLine } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const RequestCard = ({ r, answers }) => (
  <div className="border border-hairline bg-white p-6 md:p-8" data-testid="admin-request-card">
    <div className="flex items-center justify-between gap-4">
      <p className="font-serif text-xl tracking-tight">
        {r.prenom ? `${r.prenom} ` : ""}
        {r.nom}
      </p>
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-haze">{fmtDate(r.created_at)}</span>
    </div>
    <div className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-soot">
      <p><span className="text-haze">E-mail — </span>{r.email}</p>
      <p><span className="text-haze">Téléphone — </span>{r.telephone || "Non renseigné"}</p>
    </div>
    {answers && answers.length > 0 && (
      <div className="mt-4 border-t border-hairline pt-4 space-y-1">
        {answers.map((a, i) => (
          <p key={i} className="text-sm text-soot">
            <span className="text-haze">{a.question} — </span>
            <span className="text-ink font-medium">{a.choix}</span>
          </p>
        ))}
      </div>
    )}
    {r.message && (
      <p className="mt-4 border-t border-hairline pt-4 text-sm text-soot leading-relaxed">{r.message}</p>
    )}
  </div>
);

export default function Admin() {
  usePageMeta("Suivi des demandes — Les Mots pour le Dire", "");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(sessionStorage.getItem("admin_token") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (tok) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/requests`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setData(res.data);
    } catch {
      sessionStorage.removeItem("admin_token");
      setToken("");
      setError("Session expirée. Reconnectez-vous.");
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      await load(res.data.token);
    } catch {
      setError("Mot de passe incorrect.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-page">
      {!token ? (
        <div className="flex-1 flex items-center justify-center px-6 pt-24 pb-16">
          <form
            onSubmit={onLogin}
            className="w-full max-w-md border border-hairline bg-white p-8 md:p-10 text-center shadow-[0_24px_60px_rgba(20,20,20,0.06)]"
            data-testid="admin-login-form"
          >
            <p className="eyebrow">Espace privé</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight mt-3">Suivi des demandes</h1>
            <div className="mt-8 text-left">
              <label htmlFor="admin-password" className="field-label">Mot de passe</label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Saisissez votre mot de passe"
                data-testid="admin-password-input"
                className="field"
              />
              {error && <p className="mt-3 text-xs text-vermilion" data-testid="admin-error">{error}</p>}
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-button" className="btn-primary mt-6 w-full disabled:opacity-60">
              {loading ? "Chargement…" : "Voir les demandes"}
            </button>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-haze">Les Mots pour le Dire</p>
          </form>
        </div>
      ) : (
        <div className="pt-32 pb-24">
          <div className="mx-auto max-w-4xl px-6">
            <p className="eyebrow">Espace privé</p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Suivi des demandes</h1>
            <div className="mt-12 space-y-14" data-testid="admin-requests-list">
              <section>
                <h2 className="font-serif text-2xl tracking-tight">Questionnaires (QCM) <span className="text-haze font-sans text-sm">— {data ? data.qcm.length : 0}</span></h2>
                <div className="mt-6 space-y-5">
                  {data && data.qcm.map((r, i) => <RequestCard key={r.id || i} r={r} answers={r.reponses} />)}
                  {data && data.qcm.length === 0 && <p className="text-sm text-haze">Aucune demande pour le moment.</p>}
                </div>
              </section>
              <section>
                <h2 className="font-serif text-2xl tracking-tight">Formulaire de contact <span className="text-haze font-sans text-sm">— {data ? data.contact.length : 0}</span></h2>
                <div className="mt-6 space-y-5">
                  {data && data.contact.map((r, i) => <RequestCard key={r.id || i} r={r} />)}
                  {data && data.contact.length === 0 && <p className="text-sm text-haze">Aucune demande pour le moment.</p>}
                </div>
              </section>
            </div>
            <div className="mt-20 flex items-center gap-3 text-haze">
              <PenLine size={14} />
              <p className="font-mono text-[10px] uppercase tracking-[0.22em]">Les Mots pour le Dire — accès réservé</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
